import type { CalendarEvent, Reminder } from './types';

export const pad = (value: number) => String(value).padStart(2, '0');
export const dateKey = (year: number, month: number, day: number) => year * 10000 + month * 100 + day;

export function todayParts() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

export function inputDate(year: number, month: number, day: number) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function parseInputDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return { year, month, day };
}

export function reminderTimestamp(item: Reminder) {
  return new Date(item.year, item.month - 1, item.day, item.hour, item.minute).getTime();
}

export function isExpired(item: Reminder) {
  return item.repeatType === 'none' && reminderTimestamp(item) < Date.now();
}

export function isClosed(item: Reminder) {
  return !item.isEnabled && (!isExpired(item) || item.repeatType !== 'none');
}

export function isActiveReminder(item: Reminder) {
  return !isExpired(item) && !isClosed(item);
}

export function repeatLabel(value: string) {
  if (value === 'none') return '';
  if (value === 'daily') return '每天';
  if (value === 'weekly') return '每週';
  if (value === 'monthly') return '每月';
  if (value.startsWith('weekdays:')) {
    const labels = ['一', '二', '三', '四', '五', '六', '日'];
    return `每週${value.slice(9).split(',').map((day) => labels[Number(day) - 1]).join('、')}`;
  }
  const custom = value.match(/^custom:(\d+):(day|week|month)$/);
  if (custom) {
    const unit = { day: '天', week: '週', month: '月' }[custom[2]];
    return `每 ${custom[1]} ${unit}`;
  }
  return value;
}

export function countdownText(item: Reminder) {
  if (!item.isEnabled || isExpired(item)) return '';
  const difference = reminderTimestamp(item) - Date.now();
  if (difference <= 0) return '';
  const minutes = Math.floor(difference / 60_000);
  if (minutes < 60) return `· ${minutes} 分鐘後`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `· ${hours} 小時後`;
  const days = Math.floor(hours / 24);
  return `· ${days} 天後`;
}

export function eventContainsDate(event: CalendarEvent, year: number, month: number, day: number) {
  const key = dateKey(year, month, day);
  return key >= dateKey(event.startYear, event.startMonth, event.startDay) &&
    key <= dateKey(event.endYear, event.endMonth, event.endDay);
}

export function eventRange(event: CalendarEvent) {
  const start = `${event.startYear}/${pad(event.startMonth)}/${pad(event.startDay)}`;
  const end = `${event.endYear}/${pad(event.endMonth)}/${pad(event.endDay)}`;
  return start === end ? start : `${start} - ${end}`;
}

export function calendarCells(year: number, month: number) {
  const first = new Date(year, month - 1, 1);
  const start = new Date(year, month - 1, 1 - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  });
}

export function monthShift(year: number, month: number, delta: number) {
  const date = new Date(year, month - 1 + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}
